import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Target, CheckCircle2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ACTIVITE_COLORS = {
  developpement_pedagogique: { chip: 'bg-blue-100 text-blue-800 hover:bg-blue-200', dot: 'bg-blue-500', short: 'DP' },
  conge_paye: { chip: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200', dot: 'bg-emerald-500', short: 'CP' },
  default: { chip: 'bg-slate-100 text-slate-700 hover:bg-slate-200', dot: 'bg-slate-400', short: 'HP' },
};
export const activiteColor = (type) => ACTIVITE_COLORS[type] || ACTIVITE_COLORS.default;

// Dialog formateur : objectif fixé par l'admin + saisie de ce qui a été réalisé
export const ActiviteFormateurDialog = ({ activite, open, onOpenChange, canEdit = true, onSaved }) => {
  const [realise, setRealise] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRealise(activite?.realise || ''); }, [activite]);
  if (!activite) return null;
  const isDP = activite.type_affectation === 'developpement_pedagogique';
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/portail/formateur/activites/${activite.id}/realise`, { realise });
      toast.success('Réalisation enregistrée');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="activite-formateur-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Target className="w-4 h-4 text-blue-600" /> {activite.label || activite.type_affectation}</DialogTitle>
          <p className="text-xs text-slate-500 capitalize">{format(parseISO(activite.date), 'EEEE d MMMM yyyy', { locale: fr })}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="activite-objectif">
            <p className="text-xs font-semibold text-blue-800">{isDP ? 'Objectif fixé par l\'organisme' : 'Consigne'}</p>
            <p className="text-sm text-blue-900 whitespace-pre-wrap mt-1">{activite.objectif || <span className="italic text-blue-400">Aucun objectif renseigné</span>}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Ce que j'ai réalisé</Label>
            <Textarea value={realise} onChange={(e) => setRealise(e.target.value)} rows={5} disabled={!canEdit} placeholder="Décrivez le travail effectué, les livrables produits, l'avancement…" className="text-sm" data-testid="activite-realise" />
            {activite.realise_at && <p className="text-[11px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Dernière saisie le {format(parseISO(activite.realise_at), 'dd/MM/yyyy à HH:mm')}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          {canEdit && <Button type="button" size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={save} data-testid="activite-realise-save">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
